'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { CustomChatHeader } from './CustomChatHeader';
import { useParticipants, useRoomContext } from '@livekit/components-react';

export function InjectCustomChatHeader() {
  const [headerContainer, setHeaderContainer] = React.useState<HTMLElement | null>(null);
  const [originalHeader, setOriginalHeader] = React.useState<HTMLElement | null>(null);

  const room = useRoomContext();
  const participants = useParticipants();

  const roomName = room?.name || '';
  const participantCount = participants.length;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleChatClose = () => {
      let chatToggleButton = document.querySelector('[data-lk-source="chat"]') as HTMLElement;

      if (!chatToggleButton) {
        chatToggleButton = document.querySelector('.lk-chat-toggle') as HTMLElement;
      }

      if (!chatToggleButton) {
        chatToggleButton = document.querySelector(
          '[class*="chat"][class*="toggle"]',
        ) as HTMLElement;
      }

      if (!chatToggleButton) {
        const allButtons = document.querySelectorAll('button');
        for (const button of allButtons) {
          const text = button.textContent?.toLowerCase() || '';
          const ariaLabel = button.getAttribute('aria-label')?.toLowerCase() || '';
          if (text.includes('chat') || ariaLabel.includes('chat')) {
            chatToggleButton = button as HTMLElement;
            break;
          }
        }
      }

      if (chatToggleButton) {
        chatToggleButton.click();
      } else {
        console.warn('Botão de chat não encontrado para fechar');
        const chatContainer = document.querySelector('.lk-chat');
        if (chatContainer) {
          (chatContainer as HTMLElement).style.display = 'none';
        }
      }
    };

    window.addEventListener('chat-close', handleChatClose);

    const injectCustomHeader = () => {
      const chatContainer = document.querySelector('.lk-chat') as HTMLElement;
      if (!chatContainer) return false;

      const existingCustomHeader = document.querySelector('[data-custom-header-injected]');
      if (existingCustomHeader) return true;

      let chatHeader = chatContainer.querySelector('.lk-chat-header') as HTMLElement | null;

      if (!chatHeader) {
        chatHeader = chatContainer.querySelector('[class*="header"]') as HTMLElement | null;
      }

      if (!chatHeader) {
        const firstChild = chatContainer.firstElementChild as HTMLElement;
        if (
          firstChild &&
          (firstChild.tagName === 'UL' || firstChild.tagName === 'DIV') &&
          firstChild.classList.toString().includes('message')
        ) {
          chatHeader = null;
        } else if (firstChild) {
          chatHeader = firstChild;
        }
      }

      const customHeaderContainer = document.createElement('div');
      customHeaderContainer.setAttribute('data-custom-header-injected', 'true');
      customHeaderContainer.style.position = 'relative';
      customHeaderContainer.style.zIndex = '1';
      customHeaderContainer.style.top = '1rem';

      if (chatHeader) {
        setOriginalHeader(chatHeader);
        chatHeader.style.display = 'none';
        chatHeader.parentNode?.insertBefore(customHeaderContainer, chatHeader);
      } else {
        chatContainer.insertBefore(customHeaderContainer, chatContainer.firstChild);
      }

      setHeaderContainer(customHeaderContainer);
      return true;
    };

    if (injectCustomHeader()) return;

    const observer = new MutationObserver((mutations) => {
      let shouldTryInject = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.classList?.contains('lk-chat') || element.querySelector?.('.lk-chat')) {
                shouldTryInject = true;
              }
            }
          });
        }
      });

      if (shouldTryInject) {
        setTimeout(injectCustomHeader, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const interval = setInterval(() => {
      if (injectCustomHeader()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      observer.disconnect();
      clearInterval(interval);

      window.removeEventListener('chat-close', handleChatClose);

      if (originalHeader) {
        originalHeader.style.display = '';
      }

      if (headerContainer && headerContainer.parentNode) {
        headerContainer.parentNode.removeChild(headerContainer);
      }
    };
  }, []);

  return (
    <>
      {headerContainer &&
        createPortal(
          <CustomChatHeader participantCount={participantCount} roomName={roomName} />,
          headerContainer,
        )}
    </>
  );
}
